import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle, useContext } from 'react';
import PropTypes from 'prop-types';
import DataGridProvider, { DataGridContext } from './context';
import DataGridStateManager from './DataGridStateManager';
import { LicenseInfo } from '@mui/x-license';

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY || '');

// Define TypeScript types for props
interface DataGridMAIQProps {
  type: any;
  onRowClick?: (row: any) => void;
  onCellClick?: (cell: any, clickedElement?: string) => void;
  onMultiSelect?: (seletedRows: any[]) => void;
  key?: string | number;
  manualUpdateData?: (rowId: string, updates: Record<string, any>) => void;
}

// Define the ref interface
export interface DataGridMAIQRef {
  manualUpdateData: (rowId: string, updates: Record<string, any>) => void;
}

// Inner component that has access to context
const DataGridMAIQInner = forwardRef<DataGridMAIQRef, DataGridMAIQProps>(({
  type,
  onRowClick = () => {},
  onCellClick = () => {},
  onMultiSelect = () => {},
  key,
  manualUpdateData,
}, ref) => {
  const dataGridContext = useContext(DataGridContext);

  // Expose the manualUpdateData function from context through ref
  useImperativeHandle(ref, () => ({
    manualUpdateData: (rowId: string, updates: Record<string, any>) => {
      if (dataGridContext?.manualUpdateData) {
        dataGridContext.manualUpdateData(rowId, updates);
      }
    }
  }), [dataGridContext?.manualUpdateData]);

  const {
    apiDetails,
    pageMode = 'scroll',
    pageSize = 0,
    filterable = false,
    renderMode = 'server',
    editable = false,
    gridHeight,
    autoHeightBuffer = 0,
    exportable = false,
    exportFileName = 'export',
    exportColumns = 'all',
    columnResizable = true,
    isRowSelectable = true,
    isOpportunity = true,
    multiSelectable = false,
    renderCellMapping = [
      {
        field: 'name',
        renderCell: 'TEXT',
      },
    ],
    columnSortModel = [],
    visibleColumns = [],
    filterableFields = [],
  } = type;
  let apiEndpoint: string = `${apiDetails.endpoint}`;
  for (const param in apiDetails.pathParams) {
    apiEndpoint += `/${apiDetails.pathParams[param]}`;
  }

  return (
    <DataGridStateManager
      apiEndpoint={apiEndpoint}
      params={apiDetails.queryParams}
      pageMode={pageMode}
      pageSize={pageSize}
      rendermode={renderMode}
      filterable={filterable}
      editable={editable}
      gridHeight={gridHeight?.toString()}
      autoHeightBuffer={autoHeightBuffer}
      onRowClick={onRowClick}
      onMultiSelect={onMultiSelect}
      exportable={exportable}
      exportFileName={exportFileName}
      exportColumns={exportColumns}
      checkboxSelection={false}
      columnResizable={columnResizable}
      isRowSelectable={isRowSelectable}
      isOpportunity={isOpportunity}
      renderCellMapping={renderCellMapping}
      multiSelectable={multiSelectable}
      key={key}
    />
  );
});

// Main component
const DataGridMAIQ = forwardRef<DataGridMAIQRef, DataGridMAIQProps>(({
  type,
  onRowClick = () => {},
  onCellClick = () => {},
  onMultiSelect = () => {},
  key,
  manualUpdateData,
}, ref) => {
  const {
    apiDetails,
    pageMode = 'scroll',
    pageSize = 0,
    filterable = false,
    renderMode = 'server',
    editable = false,
    gridHeight,
    autoHeightBuffer = 0,
    exportable = false,
    exportFileName = 'export',
    exportColumns = 'all',
    columnResizable = true,
    isRowSelectable = true,
    isOpportunity = true,
    multiSelectable = false,
    renderCellMapping = [
      {
        field: 'name',
        renderCell: 'TEXT',
      },
    ],
    columnSortModel = [],
    visibleColumns = [],
    filterableFields = [],
  } = type;

  return (
    <DataGridProvider
      renderCellMapping={renderCellMapping}
      onCellClick={onCellClick}
      sortModel={columnSortModel}
      visibleColumns={visibleColumns}
      filterableFields={filterableFields}
      externalManualUpdate={manualUpdateData}
    >
      <DataGridMAIQInner
        type={type}
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        onMultiSelect={onMultiSelect}
        key={key}
        ref={ref}
      />
    </DataGridProvider>
  );
});

// PropTypes for runtime validation
DataGridMAIQ.propTypes = {
  type: PropTypes.any.isRequired,
};

DataGridMAIQ.displayName = 'DataGridMAIQ';

export default DataGridMAIQ;
